import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../api";
import { formatCurrency } from "../format";
import type { Row } from "../types";
import { IconAward, IconEdit, IconPlus, IconTrash, IconTrendUp, IconUsers } from "../icons";
import RecordForm from "./RecordForm";
import { compararNomes } from "../ordem";
import { modeloPorNome } from "../promocoes-modelos";
import { NOTAS_PLANO, camposMovimentacao, dataBr, dataIso, norm } from "../plano-cargos";
import type { Plano } from "./CargosView";

/**
 * MOVIMENTAÇÕES e PAINEL ANUAL do Plano de Cargos e Salários (abas
 * "Movimentações 2026" e "Painel Anual" da planilha).
 *
 * A lista junta o que foi lançado aqui com as promoções da aba Promoções
 * (GET /api/cargos-plano/movimentacoes). O painel refaz as fórmulas da
 * planilha (CONT.SES com "Confirmado? = Sim"), por ano.
 */

const MESES = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];
const ANO_ATUAL = new Date().getFullYear();

/** Eixos do plano, em ordem alfabética: são os "setores" das movimentações. */
function eixosDoPlano(plano: Plano): string[] {
  return [...new Set(plano.cargos.map((c) => String(c.eixo ?? "").trim()).filter(Boolean))].sort(compararNomes);
}

/**
 * Setor de uma movimentação: o escolhido no lançamento; numa promoção, o eixo
 * do cargo novo (direto no plano ou pela trilha onde ele aparece) e, por
 * último, o departamento do colaborador.
 */
function setorDe(m: Row, plano: Plano): string {
  if (m.setor) return String(m.setor);
  const alvo = norm(String(m.cargo_novo ?? "").split(" - ")[0]);
  const direto = plano.cargos.find((c) => norm(c.cargo) === alvo);
  if (direto?.eixo) return String(direto.eixo);
  const etapa = plano.etapas.find((e) => norm(e.cargo) === alvo);
  const trilha = etapa && plano.trilhas.find((t) => Number(t.id) === Number(etapa.trilha_id));
  const dono = trilha && plano.cargos.find((c) => norm(c.cargo) === norm(trilha.cargo_vigente));
  if (dono?.eixo) return String(dono.eixo);
  return String(m.colaborador_departamento ?? "Sem setor");
}

function useMovimentacoes(plano: Plano) {
  const [linhas, setLinhas] = useState<Row[] | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const carregar = useCallback(async () => {
    setErro(null);
    try {
      const rows = await api.planoCargos.movimentacoes();
      setLinhas(rows.map((m) => ({ ...m, setor_efetivo: setorDe(m, plano) })));
    } catch (err) {
      setErro(err instanceof Error ? err.message : "Erro ao carregar as movimentações");
      setLinhas([]);
    }
  }, [plano]);
  useEffect(() => {
    carregar();
  }, [carregar]);
  return { linhas, erro, carregar };
}

const anoDe = (m: Row) => Number(dataIso(m.data).slice(0, 4)) || null;

function SeletorAno({ linhas, ano, onAno }: { linhas: Row[]; ano: number | "todos"; onAno: (a: number | "todos") => void }) {
  const anos = [...new Set([ANO_ATUAL, ...linhas.map(anoDe).filter((a): a is number => !!a)])].sort((a, b) => b - a);
  return (
    <select
      className="plano-ano"
      value={String(ano)}
      onChange={(e) => onAno(e.target.value === "todos" ? "todos" : Number(e.target.value))}
    >
      {anos.map((a) => (
        <option key={a} value={a}>
          {a}
        </option>
      ))}
      <option value="todos">Todos os anos</option>
    </select>
  );
}

/* ------------------------------------------------------------------------ */
/* Movimentações                                                             */
/* ------------------------------------------------------------------------ */

export function MovimentacoesPlano({
  plano,
  colaboradores,
  podeEditar,
  podeExcluir,
}: {
  plano: Plano;
  colaboradores: Row[];
  podeEditar: boolean;
  podeExcluir: boolean;
}) {
  const { linhas, erro, carregar } = useMovimentacoes(plano);
  const [ano, setAno] = useState<number | "todos">(ANO_ATUAL);
  const [editando, setEditando] = useState<Row | null | undefined>(undefined);
  const campos = useMemo(() => camposMovimentacao(eixosDoPlano(plano)), [plano]);
  const visiveis = (linhas ?? []).filter((m) => ano === "todos" || anoDe(m) === ano);

  async function salvar(data: Row) {
    if (editando) await api.update("cargos_movimentacoes", editando.id, data);
    else await api.create("cargos_movimentacoes", data);
    setEditando(undefined);
    await carregar();
  }

  async function excluir(m: Row) {
    if (!confirm(`Excluir a movimentação de ${m.colaborador_nome ?? "colaborador"} (${dataBr(m.data)})?`)) return;
    try {
      await api.remove("cargos_movimentacoes", m.id);
      await carregar();
    } catch (err) {
      alert(err instanceof Error ? err.message : "Erro ao excluir");
    }
  }

  return (
    <>
      <div className="crud-toolbar">
        <SeletorAno linhas={linhas ?? []} ano={ano} onAno={setAno} />
        <div className="toolbar-right">
          <span className="badge">{visiveis.length} movimentação(ões)</span>
          {podeEditar && (
            <button className="btn-primary" onClick={() => setEditando(null)}>
              <IconPlus size={17} /> Nova movimentação
            </button>
          )}
        </div>
      </div>
      <p className="plano-nota">{NOTAS_PLANO.movimentacoes}</p>
      {erro && <div className="form-error">{erro}</div>}

      <div className="table-wrap">
        <table className="table plano-tabela">
          <thead>
            <tr>
              <th>Data</th>
              <th>Colaborador</th>
              <th>Setor</th>
              <th>Tipo</th>
              <th>Cargo anterior → novo</th>
              <th className="num">Salário anterior → novo</th>
              <th>Confirmado?</th>
              <th>Documento / evidência</th>
              <th className="col-actions" />
            </tr>
          </thead>
          <tbody>
            {linhas === null ? (
              <tr>
                <td colSpan={9} className="empty">
                  Carregando...
                </td>
              </tr>
            ) : visiveis.length === 0 ? (
              <tr>
                <td colSpan={9} className="empty">
                  Nenhuma movimentação {ano === "todos" ? "registrada" : `em ${ano}`}.
                </td>
              </tr>
            ) : (
              visiveis.map((m) => {
                const daPromocao = m.origem === "promocao";
                const modelo = daPromocao ? modeloPorNome(m.modelo)?.nome ?? m.modelo : null;
                return (
                  <tr key={`${m.origem}-${m.id}`}>
                    <td>{dataBr(m.data)}</td>
                    <td>{String(m.colaborador_nome ?? "—")}</td>
                    <td>{String(m.setor_efetivo ?? "—")}</td>
                    <td>{String(m.tipo ?? "")}</td>
                    <td className="quebra">
                      {m.cargo_anterior || m.cargo_novo
                        ? `${String(m.cargo_anterior || "—")} → ${String(m.cargo_novo || "—")}`
                        : "—"}
                    </td>
                    <td className="num">
                      {formatCurrency(m.salario_anterior)} → {formatCurrency(m.salario_novo)}
                    </td>
                    <td>
                      <span className={m.confirmado === "Sim" ? "pill pill-ok" : "pill pill-neutral"}>
                        {String(m.confirmado ?? "Não")}
                      </span>
                    </td>
                    <td className="quebra">
                      {String(m.documento ?? "—")}
                      {modelo ? ` (${modelo})` : ""}
                    </td>
                    <td className="col-actions">
                      {daPromocao ? (
                        <small className="plano-origem" title="Para alterar, use a aba Promoções">
                          aba Promoções
                        </small>
                      ) : (
                        <span className="cargo-acoes">
                          {podeEditar && (
                            <button className="icon-btn" title="Editar" onClick={() => setEditando(m)}>
                              <IconEdit size={15} />
                            </button>
                          )}
                          {podeExcluir && (
                            <button className="icon-btn icon-btn-danger" title="Excluir" onClick={() => excluir(m)}>
                              <IconTrash size={15} />
                            </button>
                          )}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {editando !== undefined && (
        <RecordForm
          title={editando ? "Editar movimentação" : "Nova movimentação"}
          fields={campos}
          initial={editando}
          colaboradores={colaboradores}
          indiceCargos={null}
          onCancel={() => setEditando(undefined)}
          onSubmit={salvar}
        />
      )}
    </>
  );
}

/* ------------------------------------------------------------------------ */
/* Painel anual                                                              */
/* ------------------------------------------------------------------------ */

export function PainelPlano({ plano }: { plano: Plano }) {
  const { linhas, erro } = useMovimentacoes(plano);
  const [ano, setAno] = useState<number | "todos">(ANO_ATUAL);

  const dados = useMemo(() => {
    const confirmadas = (linhas ?? []).filter(
      (m) => m.confirmado === "Sim" && (ano === "todos" || anoDe(m) === ano),
    );
    const aumentos = confirmadas.filter((m) => m.tipo === "Aumento salarial");
    const promocoes = confirmadas.filter((m) => m.tipo === "Promoção");

    // Todos os eixos do plano aparecem, mesmo com zero (como na planilha).
    const setores = new Map<string, number>(eixosDoPlano(plano).map((e) => [e, 0]));
    for (const m of aumentos) {
      const s = String(m.setor_efetivo ?? "Sem setor");
      setores.set(s, (setores.get(s) ?? 0) + 1);
    }
    const porMes = MESES.map(() => 0);
    for (const m of promocoes) {
      const mes = Number(dataIso(m.data).slice(5, 7));
      if (mes >= 1 && mes <= 12) porMes[mes - 1]++;
    }
    const pessoas = new Set(promocoes.map((m) => String(m.colaborador_id ?? m.colaborador_nome ?? m.id)));
    return {
      setores: [...setores.entries()],
      porMes,
      setoresComAumento: [...setores.values()].filter((n) => n > 0).length,
      pessoasPromovidas: pessoas.size,
      aumentosConfirmados: aumentos.length,
    };
  }, [linhas, ano, plano]);

  const maxSetor = Math.max(1, ...dados.setores.map(([, n]) => n));
  const maxMes = Math.max(1, ...dados.porMes);
  const periodo = ano === "todos" ? "todos os anos" : String(ano);

  return (
    <>
      <div className="crud-toolbar">
        <SeletorAno linhas={linhas ?? []} ano={ano} onAno={setAno} />
      </div>
      <p className="plano-nota">{NOTAS_PLANO.painel}</p>
      {erro && <div className="form-error">{erro}</div>}

      <div className="plano-kpis">
        <div className="plano-kpi destaque">
          <span className="plano-kpi-icone">
            <IconTrendUp size={20} />
          </span>
          <b>{dados.setoresComAumento}</b>
          <small>Setores com aumento</small>
        </div>
        <div className="plano-kpi">
          <span className="plano-kpi-icone">
            <IconAward size={20} />
          </span>
          <b>{dados.pessoasPromovidas}</b>
          <small>Pessoas promovidas</small>
        </div>
        <div className="plano-kpi">
          <span className="plano-kpi-icone">
            <IconUsers size={20} />
          </span>
          <b>{dados.aumentosConfirmados}</b>
          <small>Aumentos confirmados</small>
        </div>
      </div>

      <div className="plano-graficos">
        <section className="plano-grafico">
          <h3>Aumentos confirmados por setor — {periodo}</h3>
          <div className="plano-barras-h">
            {dados.setores.map(([setor, n]) => (
              <div key={setor} className="plano-barra-h" title={`${setor}: ${n} aumento(s) confirmado(s)`}>
                <span className="plano-barra-rotulo">{setor}</span>
                <span className="plano-barra-trilho">
                  <span className="plano-barra" style={{ width: `${(n / maxSetor) * 100}%` }} />
                </span>
                <span className="plano-barra-valor">{n}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="plano-grafico">
          <h3>Promoções confirmadas por mês — {periodo}</h3>
          <div className="plano-barras-v">
            {dados.porMes.map((n, i) => (
              <div key={MESES[i]} className="plano-coluna" title={`${MESES[i]}: ${n} promoção(ões) confirmada(s)`}>
                <span className="plano-coluna-valor">{n > 0 ? n : ""}</span>
                <span className="plano-coluna-trilho">
                  <span className="plano-coluna-barra" style={{ height: `${(n / maxMes) * 100}%` }} />
                </span>
                <span className="plano-coluna-mes">{MESES[i]}</span>
              </div>
            ))}
          </div>
        </section>
      </div>
    </>
  );
}
